<template>
  <svg :viewBox="`0 0 ${W} ${H}`" :style="{height: H + 'px', direction: 'ltr'}" class="sd-linechart">
    <template v-if="hl && hl.length">
      <rect v-for="band in highlightBands" :key="'hl' + band.y" :x="X(band.i) - bandW / 2" :y="mT - 12" :width="bandW" :height="H - mT - mB + 12" rx="8" fill="var(--sd-green-100)" opacity=".7" />
    </template>
    <g v-for="g in grid" :key="'g' + g">
      <line :x1="mS + 2" :y1="Y(g)" :x2="W - 14" :y2="Y(g)" stroke="var(--sd-line-2)" stroke-width="1" />
      <text :x="mS - 6" :y="Y(g) + 3.5" class="ax" text-anchor="end">{{ pctS(g) }}</text>
    </g>
    <text v-for="(y, i) in years" :key="'x' + y" :x="X(i)" :y="H - 8" text-anchor="middle" class="ax">{{ y }}</text>
    <g v-for="s in drawn" :key="s.name">
      <path :d="s.d" fill="none" :stroke="s.color" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      <g v-for="p in s.pts" :key="p.year">
        <circle :cx="p.x" :cy="p.y" :r="p.big ? 6.5 : 4.5" :fill="p.big && emphColor ? emphColor : s.color" stroke="var(--sd-card)" stroke-width="2.2" />
        <text v-if="p.label" :x="p.x" :y="p.ly" text-anchor="middle" class="gv" :font-size="p.big ? 13 : 10.5">{{ pctS(p.v) }}</text>
        <circle v-tip="p.tip" :cx="p.x" :cy="p.y" r="15" fill="transparent" />
      </g>
    </g>
  </svg>
</template>
<script>
/* Multi-series line chart over a shared year axis.
   series: [{ name, color, v: { [year]: value|null }, tips?: { [year]: html } }] */
import { pctS, pctHtml, esc } from '../../lib/format.js'
export default {
  name: 'SdLineChart',
  props: {
    series: { type: Array, default: () => [] },
    years: { type: Array, default: () => [] },
    hl: { type: Array, default: null },          // highlighted years (years mode)
    labels: { type: Boolean, default: false },   // label every point (single series)
    emphLast: { type: Boolean, default: false }, // bigger last point
    emphColor: { type: String, default: '' },
    lo: { type: Number, default: null }, hi: { type: Number, default: null },
    height: { type: Number, default: 230 },
  },
  data: () => ({ W: 520, mS: 44, mT: 26, mB: 30 }),
  computed: {
    // highlighted years keep their x-axis index so the band lines up with the year label
    highlightBands() { return this.years.map((y, i) => ({ y, i })).filter((band) => this.hl.includes(+band.y)) },
    H() { return this.height },
    vals() { const v = []; this.series.forEach((s) => this.years.forEach((y) => { if (s.v[y] != null) v.push(s.v[y]) })); return v },
    range() {
      if (!this.vals.length) return [0, 100]
      let lo = this.lo != null ? this.lo : Math.max(0, Math.floor((Math.min(...this.vals) - 5) / 5) * 5)
      let hi = this.hi != null ? this.hi : Math.min(100, Math.ceil((Math.max(...this.vals) + 8) / 5) * 5)
      if (hi - lo < 20) hi = Math.min(100, lo + 20)
      return [lo, hi]
    },
    grid() { const [lo, hi] = this.range; const step = (hi - lo) > 40 ? 10 : 5; const g = []; for (let v = lo; v <= hi; v += step) g.push(v); return g },
    bandW() { return (this.W - this.mS - 40) / Math.max(1, this.years.length - 1) },
    drawn() {
      const out = []; const endLabels = []
      this.series.forEach((s) => {
        let d = '', pen = false, lastIdx = -1
        this.years.forEach((y, i) => { const v = s.v[y]; if (v == null) { pen = false; return } d += (pen ? 'L' : 'M') + this.X(i) + ',' + this.Y(v) + ' '; pen = true; lastIdx = i })
        const pts = []
        this.years.forEach((y, i) => {
          const v = s.v[y]; if (v == null) return
          const big = this.hl ? this.hl.includes(+y) : (this.emphLast || this.series.length > 1) && i === lastIdx
          const p = { year: y, v, x: this.X(i), y: this.Y(v), big, label: big || this.labels, ly: this.Y(v) - (big ? 13 : 11),
            tip: (s.tips && s.tips[y]) || `<b>${esc(s.name)}</b> · ${y}<br>${pctHtml(v)}` }
          if (big && !this.hl && this.series.length > 1) endLabels.push(p)
          pts.push(p)
        })
        out.push({ name: s.name, color: s.color, d, pts })
      })
      /* push overlapping end labels apart */
      endLabels.sort((a, b) => a.ly - b.ly)
      for (let i = 1; i < endLabels.length; i++) if (endLabels[i].ly - endLabels[i - 1].ly < 15) endLabels[i].ly = endLabels[i - 1].ly + 15
      return out
    },
  },
  methods: {
    pctS,
    X(i) { return this.mS + 14 + (i / Math.max(1, this.years.length - 1)) * (this.W - this.mS - 40) },
    Y(v) { const [lo, hi] = this.range; return this.mT + (1 - (v - lo) / ((hi - lo) || 1)) * (this.H - this.mT - this.mB) },
  },
}
</script>
